import { useState, useEffect } from "react"
import styled from "styled-components"
import { Grid, List, Search, Filter, Image, X, ChevronLeft, ChevronRight, Download, Heart, MessageSquare, Calendar, User as UserIcon, Tag } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useNavigate } from "react-router-dom"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

const Title = styled.h1`
  font-size: 24px;
  margin: 0;
  color: ${props => props.theme.textPrimary};
`

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 4px;
  padding: 8px 12px;
  box-shadow: ${props => props.theme.shadow};
  flex: 1;
  max-width: 400px;
  
  @media (max-width: 640px) {
    max-width: 100%;
    width: 100%;
  }
  
  input {
    border: none;
    flex: 1;
    margin-left: 8px;
    outline: none;
    font-size: 14px;
    background: transparent;
    color: ${props => props.theme.textPrimary};
    
    &::placeholder {
      color: ${props => props.theme.textMuted};
    }
  }
`

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: ${props => props.theme.shadow};
`

const ViewToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ToggleButton = styled.button`
  background-color: ${(props) => (props.$active ? props.theme.buttonPrimary : "transparent")};
  color: ${(props) => (props.$active ? "white" : props.theme.textSecondary)};
  border: none;
  border-radius: 4px;
  padding: 6px;
  cursor: pointer;
  
  &:hover {
    background-color: ${(props) => (props.$active ? props.theme.buttonPrimaryHover : props.theme.hoverBackground)};
  }
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: transparent;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const GridView = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const ListView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PhotoCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow};
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-3px);
  }
  
  .image-container {
    position: relative;
    width: 100%;
    height: ${(props) => (props.$view === "grid" ? "160px" : "120px")};
    background-color: ${props => props.theme.borderLight};
    overflow: hidden;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    max-width: 100%;
    max-height: 100%;
  }
  
  .no-image {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    color: ${props => props.theme.textMuted};
  }
  
  .info {
    padding: 12px;
  }
  
  .title {
    font-weight: 500;
    margin-bottom: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 14px;
    height: 2.8em;
    color: ${props => props.theme.textPrimary};
  }
  
  .meta {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: ${props => props.theme.textSecondary};
  }
  
  .author {
    display: flex;
    align-items: center;
    gap: 6px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 60%;
    color: ${props => props.theme.textSecondary};
  }
  
  .avatar {
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
    border-radius: 50%;
    overflow: hidden;
    background-color: ${props => props.theme.borderLight};
    
    img {
      width: 20px;
      height: 20px;
      object-fit: cover;
    }
  }
`

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.textSecondary};
`

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.error};
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.error};
  border-radius: 8px;
`

const NoImagesMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  
  .icon {
    color: ${props => props.theme.textMuted};
    margin-bottom: 12px;
  }
  
  .text {
    color: ${props => props.theme.textSecondary};
    font-size: 16px;
  }
`

// 라이트박스 모달 스타일
const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`

const LightboxContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  overflow: hidden;
`

const LightboxImage = styled.div`
  position: relative;
  max-width: 80vw;
  max-height: 70vh;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const LightboxControls = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 8px;
`

const LightboxButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`

const LightboxNavigation = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${props => props.$direction === 'prev' ? '20px' : 'auto'};
  right: ${props => props.$direction === 'next' ? '20px' : 'auto'};
`

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`

const LightboxInfo = styled.div`
  padding: 20px;
  background-color: ${props => props.theme.cardBackground};
  max-height: 200px;
  overflow-y: auto;
`

const LightboxTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`

const LightboxMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  
  .author {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    
    &:hover {
      color: ${props => props.theme.buttonPrimary};
    }
  }
  
  .stats {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`

const FilterDropdown = styled.div`
  position: relative;
  display: inline-block;
`

const FilterMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadowHover};
  padding: 8px;
  min-width: 200px;
  z-index: 100;
  margin-top: 4px;
`

const FilterSection = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .label {
    font-size: 12px;
    font-weight: 600;
    color: ${props => props.theme.textSecondary};
    margin-bottom: 8px;
    text-transform: uppercase;
  }
`

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  input {
    margin: 0;
  }
`

export default function PhotoGallery() {
  const { isLoggedIn, user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [view, setView] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: 'all',
    author: 'all',
    hasMultipleImages: false
  });

  // 포스트 목록 가져오기 함수
  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/post/list', {
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log("포스트 목록 로드 성공:", data.posts);
        setPosts(data.posts);
      } else {
        console.error("포스트 목록 로드 실패:", data.message);
        setError(data.message || "포스트를 불러오는데 실패했습니다.");
      }
    } catch (error) {
      console.error("포스트 목록 로드 오류:", error);
      setError("서버 연결에 문제가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 포스트 목록 가져오기
  useEffect(() => {
    fetchPosts();
  }, []);

  // 이미지가 있는 포스트만 필터링
  const postsWithImages = posts.filter(post => 
    post.images && post.images.length > 0
  );

  // 필터링 로직
  const getFilteredPosts = () => {
    let filtered = postsWithImages;

    // 검색어 필터
    if (searchQuery) {
      filtered = filtered.filter(post =>
        post.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.author_username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.hashtags && post.hashtags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
      );
    }

    // 작성자 필터
    if (filters.author !== 'all') {
      if (filters.author === 'me') {
        filtered = filtered.filter(post => post.author_id === user?.id);
      }
    }

    // 다중 이미지 필터
    if (filters.hasMultipleImages) {
      filtered = filtered.filter(post => post.images && post.images.length > 1);
    }

    // 날짜 필터
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (filters.dateRange) {
        case 'today':
          filterDate.setDate(now.getDate());
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      
      if (filters.dateRange !== 'all') {
        filtered = filtered.filter(post => new Date(post.created_at) >= filterDate);
      }
    }

    return filtered;
  };

  const filteredPosts = getFilteredPosts();

  // 날짜 포맷 함수
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' });
  };

  // 한 줄 요약 생성 함수
  const getSummary = (content, maxLength = 40) => {
    if (!content) return "제목 없음";
    return content.length > maxLength 
      ? content.substring(0, maxLength) + '...' 
      : content;
  };

  // 라이트박스 열기
  const openLightbox = (post, imageIndex = 0) => {
    setSelectedImage(post);
    setCurrentImageIndex(imageIndex);
  };

  // 라이트박스 닫기
  const closeLightbox = () => {
    setSelectedImage(null);
    setCurrentImageIndex(0);
  };

  // 다음 이미지
  const nextImage = () => {
    if (selectedImage && selectedImage.images) {
      setCurrentImageIndex(prev => 
        prev < selectedImage.images.length - 1 ? prev + 1 : 0
      );
    }
  };

  // 이전 이미지
  const prevImage = () => {
    if (selectedImage && selectedImage.images) {
      setCurrentImageIndex(prev => 
        prev > 0 ? prev - 1 : selectedImage.images.length - 1
      );
    }
  };

  // 이미지 다운로드
  const downloadImage = () => {
    if (selectedImage && selectedImage.images && selectedImage.images[currentImageIndex]) {
      const link = document.createElement('a');
      link.href = selectedImage.images[currentImageIndex].url;
      link.download = `photo_${selectedImage.id}_${currentImageIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // ESC 키 핸들러
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (selectedImage) {
        if (event.key === 'Escape') {
          closeLightbox();
        } else if (event.key === 'ArrowRight') {
          nextImage();
        } else if (event.key === 'ArrowLeft') {
          prevImage();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  // 필터 변경 핸들러
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  return (
    <Container>
      <Header>
        <Title theme={theme}>사진첩</Title>
        <SearchBar theme={theme}>
          <Search size={16} color={theme.textMuted} />
          <input
            type="text"
            placeholder="사진 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBar>
      </Header>

      <Controls theme={theme}>
        <ViewToggle>
          <ToggleButton theme={theme} $active={view === "grid"} onClick={() => setView("grid")}>
            <Grid size={16} />
          </ToggleButton>
          <ToggleButton theme={theme} $active={view === "list"} onClick={() => setView("list")}>
            <List size={16} />
          </ToggleButton>
        </ViewToggle>

        <FilterDropdown>
          <FilterButton theme={theme} onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} />
            <span>필터</span>
          </FilterButton>
          {showFilters && (
            <FilterMenu theme={theme}>
              <FilterSection theme={theme}>
                <div className="label">날짜</div>
                <FilterOption theme={theme}>
                  <input 
                    type="radio" 
                    name="dateRange" 
                    value="all"
                    checked={filters.dateRange === 'all'}
                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  />
                  전체
                </FilterOption>
                <FilterOption theme={theme}>
                  <input 
                    type="radio" 
                    name="dateRange" 
                    value="today"
                    checked={filters.dateRange === 'today'}
                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  />
                  오늘
                </FilterOption>
                <FilterOption theme={theme}>
                  <input 
                    type="radio" 
                    name="dateRange" 
                    value="week"
                    checked={filters.dateRange === 'week'}
                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  />
                  최근 7일
                </FilterOption>
                <FilterOption theme={theme}>
                  <input 
                    type="radio" 
                    name="dateRange" 
                    value="month"
                    checked={filters.dateRange === 'month'}
                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  />
                  최근 30일
                </FilterOption>
              </FilterSection>
              
              {isLoggedIn && (
                <FilterSection theme={theme}>
                  <div className="label">작성자</div>
                  <FilterOption theme={theme}>
                    <input 
                      type="radio" 
                      name="author" 
                      value="all"
                      checked={filters.author === 'all'}
                      onChange={(e) => handleFilterChange('author', e.target.value)}
                    />
                    모든 사용자
                  </FilterOption>
                  <FilterOption theme={theme}>
                    <input 
                      type="radio" 
                      name="author" 
                      value="me"
                      checked={filters.author === 'me'}
                      onChange={(e) => handleFilterChange('author', e.target.value)}
                    />
                    내 사진만
                  </FilterOption>
                </FilterSection>
              )}
              
              <FilterSection theme={theme}>
                <FilterOption theme={theme}>
                  <input 
                    type="checkbox" 
                    checked={filters.hasMultipleImages}
                    onChange={(e) => handleFilterChange('hasMultipleImages', e.target.checked)}
                  />
                  다중 이미지만
                </FilterOption>
              </FilterSection>
            </FilterMenu>
          )}
        </FilterDropdown>
      </Controls>

      {loading ? (
        <LoadingIndicator>사진을 불러오는 중...</LoadingIndicator>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : filteredPosts.length === 0 ? (
        <NoImagesMessage>
          <Image size={48} className="icon" />
          <div className="text">표시할 이미지가 없습니다</div>
        </NoImagesMessage>
      ) : view === "grid" ? (
        <GridView>
          {filteredPosts.map((post) => (
            <PhotoCard key={post.id} theme={theme} $view={view} onClick={() => openLightbox(post, 0)}>
              <div className="image-container">
                {post.images && post.images.length > 0 ? (
                  <img 
                    src={post.images[0].url} 
                    alt={getSummary(post.content, 30)} 
                    loading="lazy" 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/placeholder.svg?height=160&width=200";
                    }}
                  />
                ) : (
                  <div className="no-image">
                    <Image size={24} />
                  </div>
                )}
                {post.images && post.images.length > 1 && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    +{post.images.length - 1}
                  </div>
                )}
              </div>
              <div className="info">
                <div className="title">{getSummary(post.content)}</div>
                <div className="meta">
                  <div 
                    className="author" 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/profile/${post.author_username}`);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="avatar">
                      {post.author_avatar ? (
                        <img 
                          src={post.author_avatar} 
                          alt={post.author_username}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/placeholder.svg?height=20&width=20";
                          }}
                        />
                      ) : (
                        <div style={{ width: '100%', height: '100%', backgroundColor: '#e5e7eb' }}></div>
                      )}
                    </div>
                    <span>{post.author_username}</span>
                  </div>
                  <span>{formatDate(post.created_at)}</span>
                </div>
              </div>
            </PhotoCard>
          ))}
        </GridView>
      ) : (
        <ListView>
          {filteredPosts.map((post) => (
            <PhotoCard key={post.id} theme={theme} $view={view} onClick={() => openLightbox(post, 0)}>
              <div style={{ display: "flex" }}>
                <div className="image-container" style={{ width: "120px", position: "relative" }}>
                  {post.images && post.images.length > 0 ? (
                    <img 
                      src={post.images[0].url} 
                      alt={getSummary(post.content, 30)} 
                      loading="lazy" 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/placeholder.svg?height=120&width=120";
                      }}
                    />
                  ) : (
                    <div className="no-image">
                      <Image size={24} />
                    </div>
                  )}
                  {post.images && post.images.length > 1 && (
                    <div style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      background: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontSize: '10px'
                    }}>
                      +{post.images.length - 1}
                    </div>
                  )}
                </div>
                <div className="info" style={{ flex: 1 }}>
                  <div className="title">{getSummary(post.content, 80)}</div>
                  <div className="meta">
                    <div 
                      className="author"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/profile/${post.author_username}`);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="avatar">
                        {post.author_avatar ? (
                          <img 
                            src={post.author_avatar} 
                            alt={post.author_username}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/placeholder.svg?height=20&width=20";
                            }}
                          />
                        ) : (
                          <div style={{ width: '100%', height: '100%', backgroundColor: '#e5e7eb' }}></div>
                        )}
                      </div>
                      <span>{post.author_username}</span>
                    </div>
                    <span>{formatDate(post.created_at)}</span>
                  </div>
                </div>
              </div>
            </PhotoCard>
          ))}
        </ListView>
      )}

      {/* 라이트박스 모달 */}
      {selectedImage && (
        <LightboxOverlay onClick={closeLightbox}>
          <LightboxContainer theme={theme} onClick={(e) => e.stopPropagation()}>
            <LightboxControls>
              <LightboxButton onClick={downloadImage}>
                <Download size={20} />
              </LightboxButton>
              <LightboxButton onClick={closeLightbox}>
                <X size={20} />
              </LightboxButton>
            </LightboxControls>
            
            <LightboxImage>
              {selectedImage.images && selectedImage.images[currentImageIndex] && (
                <img 
                  src={selectedImage.images[currentImageIndex].url}
                  alt={getSummary(selectedImage.content, 50)}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/placeholder.svg?height=600&width=800";
                  }}
                />
              )}
              
              {selectedImage.images && selectedImage.images.length > 1 && (
                <>
                  <LightboxNavigation $direction="prev">
                    <NavButton 
                      onClick={prevImage}
                      disabled={currentImageIndex === 0}
                    >
                      <ChevronLeft size={24} />
                    </NavButton>
                  </LightboxNavigation>
                  
                  <LightboxNavigation $direction="next">
                    <NavButton 
                      onClick={nextImage}
                      disabled={currentImageIndex === selectedImage.images.length - 1}
                    >
                      <ChevronRight size={24} />
                    </NavButton>
                  </LightboxNavigation>
                </>
              )}
            </LightboxImage>
            
            <LightboxInfo theme={theme}>
              <LightboxTitle theme={theme}>{getSummary(selectedImage.content, 100) || "사진"}</LightboxTitle>
              <LightboxMeta theme={theme}>
                <div 
                  className="author"
                  onClick={() => {
                    closeLightbox();
                    navigate(`/profile/${selectedImage.author_username}`);
                  }}
                >
                  <UserIcon size={16} />
                  {selectedImage.author_username}
                </div>
                <div>
                  <Calendar size={16} />
                  {formatDate(selectedImage.created_at)}
                </div>
                <div className="stats">
                  <div>
                    <Heart size={16} />
                    {selectedImage.like_count || 0}
                  </div>
                  <div>
                    <MessageSquare size={16} />
                    {selectedImage.comment_count || 0}
                  </div>
                </div>
                {selectedImage.images && selectedImage.images.length > 1 && (
                  <div>
                    {currentImageIndex + 1} / {selectedImage.images.length}
                  </div>
                )}
              </LightboxMeta>
              
              {selectedImage.hashtags && selectedImage.hashtags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                  {selectedImage.hashtags.map((tag, index) => (
                    <span 
                      key={index} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '12px',
                        fontSize: '12px',
                        color: '#6b7280'
                      }}
                    >
                      <Tag size={10} />
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </LightboxInfo>
          </LightboxContainer>
        </LightboxOverlay>
      )}
    </Container>
  );
}